import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, X } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "instagram_popup_seen";
const INSTAGRAM_URL = "https://instagram.com/aiplimited";

const InstagramPopup = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen) return;

    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShow(false);
  };

  const follow = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer");
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismiss}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            data-testid="instagram-popup-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 22, stiffness: 280 }}
            className="fixed left-1/2 top-1/2 z-[61] w-[92%] max-w-md -translate-x-1/2 -translate-y-1/2"
            data-testid="instagram-popup"
          >
            <div className="relative overflow-hidden rounded-2xl border border-[#B8960B]/40 bg-neutral-900 shadow-2xl">
              {/* Decorative gradient ring */}
              <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-gradient-to-br from-[#B8960B]/30 via-pink-500/10 to-purple-500/10 blur-2xl" />

              {/* Close */}
              <button
                onClick={dismiss}
                aria-label="Close"
                className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-neutral-400 transition hover:bg-neutral-800 hover:text-white"
                data-testid="instagram-popup-close"
              >
                <X size={18} />
              </button>

              <div className="relative p-8 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#B8960B] via-pink-500 to-purple-600 shadow-lg">
                  <Instagram className="text-white" size={32} />
                </div>

                <h3 className="mb-2 text-2xl font-semibold text-white">
                  Follow us on Instagram
                </h3>
                <p className="mb-6 text-sm leading-relaxed text-neutral-400">
                  See behind-the-scenes inventory work, property tips, and
                  updates from the AIP team. Join us{" "}
                  <span className="font-medium text-[#B8960B]">@aiplimited</span>.
                </p>

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                  <Button
                    variant="outline"
                    onClick={dismiss}
                    className="btn-secondary rounded-full px-6 text-sm"
                    data-testid="instagram-popup-dismiss-btn"
                  >
                    Maybe later
                  </Button>
                  <Button
                    onClick={follow}
                    className="btn-primary rounded-full px-6 text-sm"
                    data-testid="instagram-popup-follow-btn"
                  >
                    <Instagram size={16} className="mr-2" />
                    Follow @aiplimited
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InstagramPopup;
